<template>
  <div
    :class="[
      dense ? 'dense' : '',
      filterable ? 'sb-base-table2' : '',
      'sb-base-table',
      fullHeightTable ? 'sb_full_height_table' : '',
    ]"
  >
    <v-dialog v-model="openDialog" max-width="500px" persistent>
      <v-card>
        <v-card-text>
          <v-layout>
            <v-flex> {{ alertMessage }} </v-flex>
            <div>
              <v-icon class="c-pointer" @click="openDialog = false">
                close
              </v-icon>
            </div>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Column expand arrow -->
    <v-row
      v-if="showColumnsHeader && !hideColumnToggleBtn"
      class="sb_expand_column_header d-flex"
    >
      <v-col class="text-center col-12">
        <v-tooltip top>
          <template v-slot:activator="{ on, attr }">
            <v-btn depressed fab small v-bind="attr" v-on="on">
              <v-icon
                v-show="expandColumnHeader === false"
                medium
                @click="headerColumntoggle"
              >
                arrow_drop_down
              </v-icon>
              <v-icon
                v-show="expandColumnHeader === true"
                small
                @click="headerColumntoggle"
              >
                close
              </v-icon>
            </v-btn>
          </template>
          <span>Columns</span>
        </v-tooltip>
        <v-divider />
      </v-col>
    </v-row>

    <!--download and Columnfilter functionality -->
    <v-layout>
      <v-flex>
        <slot
          name="tableTopActions"
          :items="items"
          :filteredItems="filteredItems"
          :selectedItems="selectedItems"
        />
      </v-flex>
      <div
        v-if="columnFilterable || downloadable"
        class="UDM_GS_HeaderIcons d-flex align-center"
      >
        <v-btn
          v-if="columnFilterable"
          :id="`${tableId}-btn-filter_list`"
          icon
          flat
          depressed
          fab
          :disabled="disableFiltersButton"
          @click="headerColumntoggle"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on, attr }">
              <v-icon
                class="material-icons-round filter-list"
                v-bind="attr"
                v-on="on"
                >filter_list</v-icon
              >
            </template>
            <span> Choose which table columns to display </span>
          </v-tooltip>
        </v-btn>
        <v-btn
          v-if="downloadable"
          :id="`${tableId}-btn-download`"
          icon
          flat
          depressed
          fab
          class="mr-0"
          :disabled="disableDownloadButton"
          @click="exportTable"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on, attr }">
              <v-icon
                class="material-icons-round base-card-download"
                v-bind="attr"
                v-on="on"
                >download</v-icon
              >
            </template>
            <span> Download this table as a CSV file </span>
          </v-tooltip>
        </v-btn>
      </div>
    </v-layout>

    <!-- Column Button -->
    <div v-if="expandColumnHeader">
      <v-layout row wrap class="sb_columns_container">
        <v-btn
          v-for="(header, idx) in allHeaders"
          :id="`${removeSpecialCharacters(header.value)}_Header`"
          :key="idx"
          :title="tooltips[header.value]"
          :color="isUserHiddenHeader(header.value) ? 'outline' : ''"
          :dark="!isUserHiddenHeader(header.value)"
          @click="showOrHideHeader(header, allHeaders)"
        >
          {{ rename[header.text] || header.text }}
        </v-btn>
      </v-layout>
    </div>

    <v-data-table
      :id="tableId"
      :ref="tableId"
      :value="selectedItems.map((x) => x[itemKey])"
      :headers="headers"
      :hide-headers="hideHeaders"
      :items="filteredItems"
      :pagination.sync="pagination"
      :hide-actions="
        noPaging ||
        (filterServerSide
          ? serverSideTotal <= initialPageLength
          : typeof items === 'undefined' || items.length <= initialPageLength)
      "
      :select-all="selectable"
      :item-key="itemKey"
      :dark="dark"
      :rows-per-page-items="rowsperpageitems"
      :total-items="serverSideTotal"
      :loading="loading"
    >
      <template #headers="props">
        <tr>
          <th v-if="selectable" class="sb_table_selectAll_checkbox text-center">
            <span v-if="maxSelections !== 1">
              <filter-header v-if="filterable" hide-filter-input single-icon>
                <v-checkbox
                  :input-value="allSelected"
                  primary
                  :indeterminate="partiallySelected"
                  hide-details
                  class="filterable-select-all"
                  :disabled="disabled"
                  @click.stop="toggleAll"
                />
              </filter-header>
              <v-checkbox
                v-else
                :input-value="allSelected"
                primary
                :indeterminate="partiallySelected"
                hide-details
                :disabled="disabled"
                @click.prevent="toggleAll"
              />
            </span>
          </th>
          <slot name="header-left" :headers="headers" />
          <slot name="header-row" :headers="headers">
            <th
              v-for="(header, idx) in headers"
              :key="idx"
              :colspan="header.colspan || 1"
              :style="{ padding: '2px' }"
              :class="{ count_column: getColumnType(header.value) !== 'text' }"
              :title="tooltips[header.value]"
            >
              <slot
                :name="`${header.value}-header`"
                :value="header.text"
                :header="header"
                :headers="headers"
                @click="changeSort(header)"
              >
                <filter-header
                  v-if="filterable"
                  :key="`${filters[header.value]}`"
                  v-model="filters[header.value]"
                  :label="rename[header.text] || header.text"
                  :filter-type="filterTypes[header.value]"
                  :is-sorted="pagination.sortBy === `[${header.value}]`"
                  :type="getColumnType(header.value)"
                  :value="filters[header.value]"
                  :sort-dir="
                    pagination.sortBy === `[${header.value}]`
                      ? sortDir
                      : 'default'
                  "
                  :previous-header-name="previousHeader"
                  :hide-filter-input="hideFilters.includes(header.value)"
                  @updated:sort="(e) => updateSortDir(header.value, e)"
                  @updated:filter="(e) => updateFilter(header.value, e)"
                  @change:header="(e) => updatePreviousHeader(e)"
                >
                  <slot :name="`${header.value}-header-text`" />
                </filter-header>
                <v-layout v-else align-center>
                  <v-layout
                    v-if="sortable"
                    column
                    class="sb_basetable_sorticons"
                  >
                    <v-icon
                      :color="
                        pagination.sortBy === `[${header.value}]`
                          ? sortDir === 'asc'
                            ? 'warning'
                            : undefined
                          : undefined
                      "
                      @click="updateSortDir(header.value, 'asc')"
                      >expand_less</v-icon
                    ><v-icon
                      :color="
                        pagination.sortBy === `[${header.value}]`
                          ? sortDir === 'desc'
                            ? 'warning'
                            : undefined
                          : undefined
                      "
                      @click="updateSortDir(header.value, 'desc')"
                      >expand_more</v-icon
                    >
                  </v-layout>
                  <v-flex>{{ rename[header.text] || header.text }}</v-flex>
                </v-layout>
              </slot>
            </th>
          </slot>
          <slot name="header-right" :header="headers" />
          <th v-if="doAction" id="actionHeaderColumn" colspan="1">
            <filter-header hide-filter-input label="Action" />
          </th>
        </tr>
      </template>
      <template #items="props">
        <tr
          :active="selected.includes(props.item[itemKey])"
          :class="
            selected.includes(props.item[itemKey])
              ? ''
              : backgroundColorFunction(props.item)
          "
          @click="toggleItem(props.item, $event.target)"
          @mouseenter="mouseEntered($event, props.item)"
          @mouseleave="mouseLeave($event, props.item)"
        >
          <td v-if="selectable" class="sb_table_checkbox">
            <v-checkbox
              v-if="props.item.selectable !== false && !disableSelect"
              :input-value="selected.includes(props.item[itemKey])"
              primary
              hide-details
              :disabled="
                props.item.selectable === false ||
                disabled === true ||
                disableSelect
              "
            />
            <v-icon
              v-else
              :class="props.item.noSelectIconColor"
              :title="
                props.item.noSelectMessage || 'This item is not selectable'
              "
            >
              {{ props.item.noSelectIcon || SbIcons.status.warn }}
            </v-icon>
          </td>
          <slot name="body-left" :item="props.item" />
          <td v-for="(header, idx) in headers" :key="idx" class="cellTextWrap">
            <!-- Dynamic scoped slot for customizing cell content-->
            <v-tooltip
              :right="!topTooltip && idx == 0"
              :left="!topTooltip && idx != 0"
              :top="topTooltip"
              max-width="50%"
            >
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <slot
                    :name="`${header.value}-body`"
                    :value="props.item[header.value]"
                    :item="props.item"
                  >
                    {{
                      getDisplayValue(
                        props.item[header.value],
                        types[header.value]
                      )
                    }}
                  </slot>
                </span>
              </template>
              <span class="wrapToolTip">
                {{
                  getDisplayValue(props.item[header.value], types[header.value])
                }}
              </span>
            </v-tooltip>
          </td>
          <slot name="body-right" :item="props.item" />
          <td v-if="doAction">
            <slot name="action-text" :item="props.item">
              <v-icon small @click="deleteItem(item)"> delete </v-icon>
            </slot>
          </td>
        </tr>
      </template>
      <template
        v-if="hasFooterSlot || displayTotals || exportable || tableOptions"
        #footer
      >
        <tr v-if="displayTotals">
          <td v-if="selectable" />
          <td v-for="(header, idx) in headers" :key="idx">
            <span v-if="idx === 0">
              <b>Grand Total</b>
            </span>
            <span v-else-if="itemTotals[header.value] !== undefined">
              <b>{{
                getDisplayValue(itemTotals[header.value], types[header.value])
              }}</b>
            </span>
          </td>
        </tr>
        <tr v-if="hasFooterSlot || exportable || tableOptions">
          <td
            :colspan="
              headers.length + (selectable ? 1 : 0) + (extraColumn ? 1 : 0)
            "
          >
            <slot
              name="footer"
              :items="items"
              :filteredItems="filteredItems"
              :selectedItems="selectedItems"
            />
            <template v-if="exportable">
              <v-btn
                v-show="showDownloadIcon"
                title="Download CSV"
                @click="exportTable"
              >
                <v-icon>
                  {{ SbIcons.export.default }}
                </v-icon>
              </v-btn>
            </template>
          </td>
        </tr>
      </template>
      <template #no-data>
        <v-alert
          value="true"
          color="#fbfbfb"
          icon="warning"
          class="sb_NoDataAvialable"
        >
          <slot name="default">
            <slot name="no-data">
              <span v-if="loading"> Loading... </span>
              <span v-else> No Data Available </span>
            </slot>
          </slot>
        </v-alert>
      </template>
      <template #pageText="{ pageStart, pageStop, itemsLength }">
        <slot
          name="page-text"
          :pageStart="pageStart"
          :pageStop="pageStop"
          :itemsLength="itemsLength"
        >
          {{ pageStart }} - {{ pageStop }} of {{ itemsLength }}
        </slot>
      </template>
    </v-data-table>
    <v-snackbar v-model="snackbar" top color="warning">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
/**
 * Display data in a table, with slots for deeper control and optional
 * header filters. The first item in the dataset is assumed to have the
 * same keys as the rest of the items, and the keys are used as the headers.
 * Headers can be hidden through the `hide` props, which accepts either a
 * regular expression to match against, or a list of the columns
 */

/* Basic usage with some custom slots and filterable headers
<base-table
  :items="[{_id: 0, Name: 'Josh', Title: 'Developer'}]"
  :hide="['_id']"
  :filterable="true"
>
  <template #Title-header="{ value }">
    H: {{ value }}
  </template>
  <template #Name-body="{ value }">
    Name: {{ value }}
  </template>
  <template #footer="props">
    This table has {{ props.items.length }} items
  </template>
</base-table>
*/
import FilterHeader from './FilterHeader.vue';
import displayTotals from './displayTotals.mixin.js';
import filterable from './filterable.mixin.js';
import typeDisplay from './typeDisplay.mixin.js';
import exportable from './exportable.mixin.js';
import selectable from './selectable.mixin.js';
import tableOptions from './tableOptions.mixin.js';
import { arrayOfObjects } from '../../static/propValidators.js';
import { arrayLooper } from '../../utility/generators.js';

export default {
  name: 'BaseTable',
  components: {
    FilterHeader,
  },
  mixins: [
    displayTotals,
    filterable,
    typeDisplay,
    exportable,
    selectable,
    tableOptions,
  ],
  props: {
    items: {
      type: Array,
      required: true,
      validator: arrayOfObjects,
    },
    disableFiltersButton: {
      type: Boolean,
    },

    disableDownloadButton: {
      type: Boolean,
    },
    // what columns to hide
    hide: {
      type: Array,
      default() {
        return [];
      },
      validator(hideArray) {
        for (let item of hideArray) {
          let isRegex = item instanceof RegExp; // note: the typeof for a RegExp is an object
          let isString = typeof item === 'string';
          if (!isRegex && !isString) {
            console.warn(
              `BaseTable hide prop: Expected array of RegExp or strings, not "${typeof item}"`
            );
            return false;
          }
        }
        return true;
      },
    },
    pageLength: {
      type: Number,
      default() {
        return 10;
      },
    },
    hideColumnToggleBtn: {
      type: Boolean,
    },
    fullHeightTable: {
      type: Boolean,
    },
    belowTableElementsHeight: {
      type: Number,
      default() {
        return 0;
      },
    },
    filterable: {
      type: Boolean,
    },
    downloadable: {
      type: Boolean,
    },
    columnFilterable: {
      type: Boolean,
    },
    /**
     * An array of headers to hide the filter input for
     */
    hideFilters: {
      type: Array,
      default: () => [],
    },
    rename: {
      type: Object,
      default() {
        return {};
      },
    },
    noPaging: {
      type: Boolean,
    },
    dense: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
    },
    hideHeaders: {
      type: Boolean,
    },
    dark: {
      type: Boolean,
    },
    defaultHeaders: {
      type: Array,
      default() {
        return [];
      },
    },
    tableId: {
      type: String,
      default() {
        return 'basetable';
      },
    },
    tooltips: {
      type: Object,
      default() {
        return {};
      },
    },
    disabled: {
      type: Boolean,
    },
    topTooltip: {
      type: Boolean,
    },
    rowsperpageitems: {
      type: Array,
      default() {
        return [5, 10, 25, 50, 100, 250];
      },
    },
    showShortenPathPref: {
      type: Boolean,
    },
    backgroundColorFunction: {
      type: Function,
      default() {
        return function () {
          return '';
        };
      },
    },
    doAction: {
      type: Boolean,
    },
    disableSelect: {
      type: Boolean,
    },
    extraColumn: {
      type: Boolean,
    },
    sortable: {
      type: Boolean,
    },
    showColumnsHeader: {
      type: Boolean,
    },
    showDownloadIcon: {
      type: Boolean,
    },
  },
  data() {
    return {
      expandColumnHeader: false,
      pagination: {
        sortBy: undefined,
        descending: true,
        page: 1,
        rowsPerPage: this.pageLength,
        tableId: this.tableId,
      },
      sortDir: undefined,
      previousSortOrder: '',
      previousHeader: null,
      openDialog: false,
      alertMessage: 'You must select at least one column',
      sortAscDirLoop: arrayLooper(['asc', 'default']),
      sortDescDirLoop: arrayLooper(['desc', 'default']),
      lastPopulatedHeaders: [],
      initialPageLength: 10,
    };
  },
  computed: {
    showFullSharePaths() {
      return this.$store.getters.hasFullSharePaths;
    },
    hasFooterSlot() {
      return !!this.$scopedSlots.footer;
    },
    allHeaders() {
      let headerArray = [];
      if (typeof this.items === 'undefined' || this.items.length < 1) {
        if (
          typeof this.defaultHeaders === 'undefined' ||
          this.defaultHeaders.length < 1
        ) {
          return [];
        }
        headerArray = this.defaultHeaders;
      } else {
        const firstItem = this.items[0];
        headerArray = Object.keys(firstItem);
      }
      const displayKeys = headerArray.filter(
        (h) => !this.isFullyHiddenHeader(h)
      );
      return displayKeys.map((value) => {
        const text = this.rename[value] || value;
        return { text, value };
      });
    },
    displayedHeaders() {
      return this.allHeaders.filter((h) => !this.isHiddenHeader(h.value));
    },
    headers() {
      if (this.displayedHeaders.length === 0) {
        return this.lastPopulatedHeaders;
      }
      return this.displayedHeaders;
    },
  },
  watch: {
    headers: {
      // Note: purposely NOT using `deep: true` here for performance
      immediate: true,
      handler(newHeaders) {
        if (newHeaders.length > 0) {
          this.lastPopulatedHeaders = newHeaders;
        }
      },
    },
    pagination: {
      handler(newValue) {
        this.$emit('update:pagination', newValue);
      },
      deep: true,
    },
    sortDir(val) {
      this.previousSortOrder = val;
    },
    selectedItems(val) {
      this.$emit('selected-items:count', val.length);
      this.$emit('selected-items', val);
    },
    items(newVal) {
      this.filteredItems = newVal;
    },
  },
  mounted() {
    this.initialPageLength = this.pageLength;
  },
  updated() {
    if (
      this.noPaging &&
      typeof this.pagination !== 'undefined' &&
      typeof this.pagination.rowsPerPage !== 'undefined' &&
      this.pagination.rowsPerPage >= 0
    )
      this.pagination.rowsPerPage = -1;
    if (this.fullHeightTable) {
      this.setTableHeight();
    }
  },
  methods: {
    setTableHeight() {
      let tableElement =
        this.$refs[this.tableId].$el.querySelectorAll('.v-table__overflow')[0];
      let tablePositionTop = tableElement.getBoundingClientRect().top,
        tableFooterHeight = this.noPaging
          ? 0
          : this.items.length > 10
          ? document.querySelector(
              '.sb_full_height_table.sb-base-table .v-datatable__actions'
            ).offsetHeight
          : 0,
        spacingBelowCard = 28 + Number(this.belowTableElementsHeight),
        applicationFooterHeight =
          document.querySelector('footer.v-footer').clientHeight,
        calculatedHeight =
          'calc(100vh - ' +
          (tablePositionTop +
            tableFooterHeight +
            spacingBelowCard +
            applicationFooterHeight) +
          'px)';
      tableElement.style.maxHeight = calculatedHeight;
    },
    // Header Column Toggle
    headerColumntoggle() {
      this.expandColumnHeader = !this.expandColumnHeader;
    },
    updateSortDir(column, sortOrder) {
      if (
        this.previousHeaderName === column &&
        this.previousSortOrder === sortOrder
      ) {
        this.sortDir =
          sortOrder == 'asc'
            ? this.sortAscDirLoop.next()
            : this.sortDescDirLoop.next();
      } else {
        this.sortDir =
          sortOrder == 'asc'
            ? this.sortAscDirLoop.first()
            : this.sortDescDirLoop.first();
      }
      this.previousHeaderName = column;
      this.previousSortOrder = sortOrder;
      this.updateSort(column, this.sortDir);
    },
    updateSort(column, sortDir) {
      // if the values are not the same
      column = this.customSortHeaders[column] || column;
      if (sortDir === 'default') {
        this.pagination.sortBy = undefined;
        this.pagination.descending = undefined;
      } else {
        this.pagination.sortBy = `[${column}]`;
        this.pagination.descending = sortDir === 'desc';
      }
    },
    updatePreviousHeader(column) {
      this.previousHeader = column;
    },
    changeSort({ value }) {
      value = this.customSortHeaders[value] || value;
      if (`[${value}]` === this.pagination.sortBy) {
        // already sorted by this column - change direction
        this.pagination.descending = !this.pagination.descending;
      } else {
        // new column to sort by
        this.pagination.sortBy = `[${value}]`;
        this.pagination.descending = true;
      }
    },
    // "fully" hidden headers are always hidden from the user
    isFullyHiddenHeader(headerKey) {
      // not hidden unless there's a match
      let hidden = false;
      for (let item of this.hide) {
        if (item instanceof RegExp) {
          // test for a regex match
          if (item.test(headerKey)) {
            hidden = true;
          }
        } else if (item === headerKey) {
          // check equality if it isn't regex
          hidden = true;
        }
      }
      return hidden;
    },
    isHiddenHeader(headerKey) {
      if (this.isUserHiddenHeader(headerKey)) {
        return true;
      }
      return this.isFullyHiddenHeader(headerKey);
    },

    mouseEntered(event, rowData) {
      event.target.style.backgroundColor = '';
      this.$emit('mouseEnter', rowData);
    },
    mouseLeave(event, item) {
      // event.target.style.backgroundColor = this.selected.includes(item[itemKey]) ? '' : backgroundColorFunction(item);
      this.$emit('mouseLeave', item);
    },
    removeSpecialCharacters(str) {
      return str
        .replace(/(?!\w|\s)./g, '')
        .replace(/\s+/g, '')
        .replace(/^(\s*)([\W\w]*)(\b\s*$)/g, '$2');
    },
  },
};
</script>

<style>
@import '../../static/css/SPHERETable.css';
td.cellTextWrap {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wrapToolTip {
  overflow-wrap: anywhere;
}
</style>
